import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import type { Filial, FilialContextType } from '@/types';
import { useFiliais } from '@/hooks/useFiliais';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';

const FilialContext = createContext<FilialContextType | undefined>(undefined);

export const FilialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const adminRoutesWithFilialSelector = ['/admin/ponto-escalas'];
  const shouldEnableFiliais = !isAdminRoute || adminRoutesWithFilialSelector.some((route) => location.pathname.startsWith(route));
  const { filiais, isLoading, error } = useFiliais({ enabled: shouldEnableFiliais }); // filiais: Filial[]
  const [filialSelecionada, setFilialSelecionada] = useState<Filial | null>(null);

  // Inicializa a filial selecionada a partir do localStorage ou primeira dispon�vel
  useEffect(() => {
    if (!filiais || filiais.length === 0) return;

    const salva = localStorage.getItem('filialSelecionada');
    if (salva) {
      try {
        const parsed = JSON.parse(salva) as { id: string };
        const encontrada = filiais.find((f) => f.id === parsed.id);
        if (encontrada) {
          setFilialSelecionada(encontrada);
          return;
        }
      } catch {
        localStorage.removeItem('filialSelecionada');
      }
    }

    setFilialSelecionada(filiais[0]);
  }, [filiais]);

  const selecionarFilial = useCallback((filial: Filial) => {
    setFilialSelecionada(filial);
    localStorage.setItem('filialSelecionada', JSON.stringify(filial));
    toast.success(`Filial ${filial.nome} selecionada`);
  }, []);

  // Compat�vel com a interface do contexto (a recarga real � feita pelo React Query)
  const carregarFiliais = useCallback(async () => {
    // no-op: deixar aqui se a interface do contexto exigir
  }, []);

  const value: FilialContextType = useMemo(
    () => ({
      filialSelecionada,
      filiais, // Filial[]
      selecionarFilial,
      carregarFiliais,
      isLoading,
      error: error ? String(error) : null,
    }),
    [filialSelecionada, filiais, selecionarFilial, carregarFiliais, isLoading, error]
  );

  return <FilialContext.Provider value={value}>{children}</FilialContext.Provider>;
};

export const useFilialContext = (): FilialContextType => {
  const ctx = useContext(FilialContext);
  if (!ctx) {
    throw new Error('useFilialContext deve ser usado dentro de FilialProvider');
  }
  return ctx;
};
